/**
 * Sign-in security cards (AUTH-MFA-01, 2026-09-02; split by SETTINGS-ACCESS-01
 * 2026-09-03 — the section itself is gone):
 *   MyTwoFactorCard   — two-factor for YOUR account; lives in the user menu ("My sign-in").
 *   RequireForAdminsCard — company rule; lives on People & Access → Sign-in rules.
 * Admin resets of someone ELSE's authenticator are on their person panel.
 */
import { useState } from "react";
import { Check, KeyRound, ShieldCheck, ShieldOff, Loader2 } from "lucide-react";
import { InfoTip } from "@/components/InfoTip";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useOrgSettingJson, useSaveOrgSetting } from "@/lib/orgSettings";
import { useMfaStatus, unenroll, useInvalidateMfa } from "@/features/auth/mfa";
import { TotpEnrollPanel } from "@/features/auth/TotpEnrollPanel";

export function MyTwoFactorCard() {
  const status = useMfaStatus();
  const invalidate = useInvalidateMfa();
  const [removing, setRemoving] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const s = status.data;

  async function remove() {
    if (!s?.factorId) return;
    setError(null);
    setRemoving(true);
    try {
      await unenroll(s.factorId);
      setConfirm(false);
      invalidate();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRemoving(false);
    }
  }

  return (
    <div className="rounded-xl border border-hairline bg-white p-5 shadow-e1">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <KeyRound className="h-4 w-4 text-brand-600" /> Two-Factor Sign-In for Your Account
        <InfoTip label="How two-factor works">
          With two-factor on, signing in takes your password plus a 6-digit code from an authenticator app on your phone. A stolen
          password alone no longer opens Pegasus.
        </InfoTip>
      </h3>
      {status.isLoading || !s ? (
        <p className="mt-3 inline-flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…</p>
      ) : s.enrolled ? (
        <div className="mt-3">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[12.5px] font-semibold text-emerald-700">
            <ShieldCheck className="h-3.5 w-3.5" /> On — authenticator app added
          </span>
          <div className="mt-3">
            {!confirm ? (
              <button type="button" onClick={() => setConfirm(true)}
                className="inline-flex items-center gap-1.5 rounded-md border border-hairline px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
                <ShieldOff className="h-3.5 w-3.5" /> Remove the authenticator
              </button>
            ) : (
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="text-slate-600">Sign-in goes back to password only. Remove it?</span>
                <button type="button" disabled={removing} onClick={remove}
                  className="rounded-md bg-red-600 px-3 py-1.5 font-semibold text-white hover:bg-red-700 disabled:opacity-40">Remove</button>
                <button type="button" onClick={() => setConfirm(false)} className="font-medium text-slate-500 hover:text-slate-800">Keep it</button>
              </div>
            )}
            {error && <p className="mt-2 text-xs text-red-700">{error}</p>}
          </div>
        </div>
      ) : (
        <div className="mt-3">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-[12.5px] font-semibold text-slate-600">Off — password only</span>
          <TotpEnrollPanel />
        </div>
      )}
    </div>
  );
}

export function RequireForAdminsCard() {
  const canManage = useCan("settings.manage");
  const current = useOrgSettingJson<boolean>("mfa_required_admins", false).data ?? false;
  const save = useSaveOrgSetting();
  const [val, setVal] = useState<boolean | null>(null);
  const v = val ?? current;
  const dirty = v !== current;
  return (
    <div className="mt-4 rounded-xl border border-hairline bg-white p-5 shadow-e1">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <ShieldCheck className="h-4 w-4 text-brand-600" /> Require Two-Factor for Admins
        <InfoTip label="About the admin two-factor rule">
          When on, every admin without an authenticator must add one at their next sign-in; techs and office roles aren't
          affected. For a lost phone, open the person on the People tab and choose "Remove authenticator"; they set it up again
          at their next sign-in.
        </InfoTip>
      </h3>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-2 text-sm text-slate-800">
          <input type="checkbox" checked={v} disabled={!canManage} onChange={(e) => setVal(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-400" />
          Required for admins
        </label>
        {canManage && (
          <button type="button" disabled={!dirty || save.isPending}
            onClick={async () => { await save.mutateAsync({ key: "mfa_required_admins", value: v }); setVal(null); }}
            className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-800 disabled:opacity-40">
            <Check className="h-3.5 w-3.5" /> Save
          </button>
        )}
      </div>
    </div>
  );
}
